import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { membrosApi, utilsApi, igrejasApi, cargosApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Save, Loader2, Search, X, Users, UserPlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const estadoCivilOptions = [
  { value: 'solteiro', label: 'Solteiro(a)' },
  { value: 'casado', label: 'Casado(a)' },
  { value: 'divorciado', label: 'Divorciado(a)' },
  { value: 'viuvo', label: 'Viúvo(a)' },
  { value: 'uniao_estavel', label: 'União Estável' },
];

const tipoSanguineoOptions = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const grauInstrucaoOptions = [
  'Fundamental Incompleto',
  'Fundamental Completo',
  'Médio Incompleto',
  'Médio Completo',
  'Superior Incompleto',
  'Superior Completo',
  'Pós-Graduação',
  'Mestrado',
  'Doutorado',
];

// Funções de máscara
const maskCPF = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 11) {
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return value;
};

const maskRG = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 9) {
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1})$/, '$1-$2');
  }
  return value;
};

const maskTelefone = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 10) {
    // Telefone fixo: (00) 0000-0000
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  } else if (numbers.length <= 11) {
    // Celular: (00) 00000-0000
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
  }
  return value;
};

// Função para remover máscaras (apenas números)
const removeMask = (value) => {
  if (!value) return value;
  return value.toString().replace(/\D/g, '');
};

export const MembroForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [igrejas, setIgrejas] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [membros, setMembros] = useState([]);
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    foto_url: '',
    nosso_numero: '',
    data_nascimento: '',
    estado_civil: '',
    sexo: '',
    obreiro: false,
    cargo_id: '',
    data_batismo: '',
    deseja_recibo_digital: false,
    rg: '',
    orgao_rg: '',
    cpf: '',
    cnh: '',
    naturalidade: '',
    nacionalidade: 'Brasileira',
    grau_instrucao: '',
    formacao_academica: '',
    tipo_sanguineo: '',
    nome_pai: '',
    nome_mae: '',
    id_pai: '',
    id_mae: '',
    conjuge_nome: '',
    conjuge_id: '',
    conjuge_membro: false,
    data_casamento: '',
    qtd_filhos: 0,
    filhos_ids: [],
    filho_membro: false,
    cabeca_familia: false,
    grupo_familiar: null,
    email: '',
    telefone_residencial: '',
    telefone_whatsapp: '',
    endereco: {
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
    },
    profissao: '',
    empresa: '',
    igreja_id: user?.igreja_id || '',
  });

  useEffect(() => {
    fetchIgrejas();
    fetchCargos();
    fetchMembros();
    if (isEditing) {
      fetchMembro();
    }
  }, [id]);

  const fetchIgrejas = async () => {
    try {
      const response = await igrejasApi.list();
      setIgrejas(response.data || []);
    } catch (error) {
      console.error('Error fetching igrejas:', error);
    }
  };

  const fetchCargos = async () => {
    try {
      const response = await cargosApi.list({
        igreja_id: user?.igreja_id,
      });
      setCargos(response.data || []);
    } catch (error) {
      console.error('Error fetching cargos:', error);
    }
  };

  const fetchMembros = async () => {
    try {
      const response = await membrosApi.list({
        igreja_id: user?.igreja_id,
        ativo: true,
      });
      // Filtrar o membro atual se estiver editando
      const membrosList = response.data || [];
      if (isEditing && id) {
        // Filtrar o membro atual, mas manter todos os outros (incluindo filhos e cônjuge)
        const currentId = typeof id === 'string' ? id : id.toString();
        setMembros(membrosList.filter(m => {
          const membroId = typeof m.id === 'string' ? m.id : m.id?.toString();
          return membroId !== currentId;
        }));
      } else {
        setMembros(membrosList);
      }
    } catch (error) {
      console.error('Error fetching membros:', error);
    }
  };

  const fetchMembro = async () => {
    try {
      setLoading(true);
      const response = await membrosApi.get(id);
      // Processar grupo_familiar se existir
      const grupoFamiliar = response.data.grupo_familiar || null;
      const membroId = response.data.id || response.data._id;
      
      const membroData = {
        ...response.data,
        endereco: response.data.endereco || {
          cep: '',
          logradouro: '',
          numero: '',
          complemento: '',
          bairro: '',
          cidade: '',
          estado: '',
        },
        grupo_familiar: grupoFamiliar,
        // Mapear campos do grupo_familiar para os campos do formulário
        // Para recuperar: pegar conjuge_id do grupo_familiar quando conjuge_membro for true
        conjuge_id: (() => {
          if (response.data.conjuge_membro && grupoFamiliar?.conjuge_id) {
            return grupoFamiliar.conjuge_id.toString();
          }
          return '';
        })(),
        id_pai: grupoFamiliar?.id_pai || '',
        id_mae: grupoFamiliar?.id_mae || '',
        // Verificar cabeca_familia: pode vir direto do membro ou do grupo_familiar
        cabeca_familia: response.data.cabeca_familia || 
          (grupoFamiliar?.id_cabeca === membroId?.toString() || grupoFamiliar?.id_cabeca === membroId) || 
          false,
        // Processar filhos do grupo_familiar
        filhos_ids: (() => {
          if (grupoFamiliar?.filhos && Array.isArray(grupoFamiliar.filhos)) {
            // Se vier como array de objetos [{filho_id: "..."}] ou array de IDs
            return grupoFamiliar.filhos.map(f => {
              if (typeof f === 'object' && f.filho_id) {
                return f.filho_id.toString();
              }
              if (typeof f === 'object' && f.id) {
                return f.id.toString();
              }
              return f.toString();
            });
          }
          // Fallback para formato antigo
          if (response.data.filhos && Array.isArray(response.data.filhos)) {
            return response.data.filhos.map(f => {
              if (typeof f === 'object' && f.filho_id) {
                return f.filho_id.toString();
              }
              return f.toString();
            });
          }
          if (response.data.filhos_ids && Array.isArray(response.data.filhos_ids)) {
            return response.data.filhos_ids.map(id => id.toString());
          }
          return [];
        })(),
      };
      // Aplicar máscaras nos campos que vêm do backend
      if (membroData.cpf) {
        membroData.cpf = maskCPF(membroData.cpf);
      }
      if (membroData.rg) {
        membroData.rg = maskRG(membroData.rg);
      }
      if (membroData.telefone_residencial) {
        membroData.telefone_residencial = maskTelefone(membroData.telefone_residencial);
      }
      if (membroData.telefone_whatsapp) {
        membroData.telefone_whatsapp = maskTelefone(membroData.telefone_whatsapp);
      }
      // Garantir que cargo_id seja string para o Select
      if (membroData.cargo_id !== undefined && membroData.cargo_id !== null) {
        membroData.cargo_id = membroData.cargo_id.toString();
      } else {
        membroData.cargo_id = '';
      }
      // Garantir que conjuge_id seja string
      if (membroData.conjuge_id) {
        membroData.conjuge_id = membroData.conjuge_id.toString();
      }
      // filhos_ids já foi processado acima como array de strings
      if (!Array.isArray(membroData.filhos_ids)) {
        membroData.filhos_ids = [];
      }
      
      // Atualizar qtd_filhos se houver filhos_ids
      if (membroData.filhos_ids.length > 0 && membroData.cabeca_familia) {
        membroData.qtd_filhos = membroData.filhos_ids.length;
      }
      
      // Carregar lista de membros ANTES de setar formData para garantir que os filhos possam ser encontrados
      await fetchMembros();
      
      setFormData(membroData);
      if (membroData.foto_url) {
        setFotoPreview(membroData.foto_url);
      }
    } catch (error) {
      toast.error('Erro ao carregar membro');
      navigate('/membros');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('endereco.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        endereco: { ...formData.endereco, [field]: value },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleMaskedChange = (e, maskFn) => {
    const { name, value } = e.target;
    const maskedValue = maskFn(value);
    setFormData({ ...formData, [name]: maskedValue });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (name, checked) => {
    if (name === 'conjuge_membro' && !checked) {
      // Se desmarcar "cônjuge é membro", limpar o conjuge_id
      setFormData({ ...formData, [name]: checked, conjuge_id: '', conjuge_nome: '' });
    } else if (name === 'conjuge_membro' && checked) {
      // Se marcar "cônjuge é membro", limpar o conjuge_nome
      setFormData({ ...formData, [name]: checked, conjuge_nome: '' });
    } else if (name === 'cabeca_familia' && !checked) {
      // Se desmarcar "cabeça da família", limpar filhos
      setFormData({ ...formData, [name]: checked, filhos_ids: [], qtd_filhos: 0 });
    } else if (name === 'filho_membro') {
      console.log('🔘 Checkbox filho_membro alterado:', checked);
      console.log('  - filhos_ids atual:', formData.filhos_ids);
      if (!checked) {
        // Se desmarcar "filho é membro", limpar filhos
        setFormData({ ...formData, [name]: checked, filhos_ids: [], qtd_filhos: 0 });
        console.log('✅ filho_membro desmarcado - filhos limpos');
      } else {
        setFormData({ ...formData, [name]: checked });
        console.log('✅ filho_membro marcado');
      }
    } else {
      setFormData({ ...formData, [name]: checked });
    }
  };

  const handleAddFilho = (filhoId) => {
    console.log('👶 handleAddFilho chamado com filhoId:', filhoId);
    console.log('  - Tipo do filhoId:', typeof filhoId);
    console.log('  - filhos_ids atual:', formData.filhos_ids);
    console.log('  - filho_membro:', formData.filho_membro);
    
    if (!filhoId) {
      console.log('❌ filhoId é vazio/null/undefined');
      return;
    }
    
    // Garantir que filhoId seja string para comparação
    const filhoIdStr = filhoId.toString();
    const filhosIdsStr = formData.filhos_ids.map(id => id.toString());
    
    console.log('  - filhoId (string):', filhoIdStr);
    console.log('  - filhos_ids (strings):', filhosIdsStr);
    console.log('  - Já existe?', filhosIdsStr.includes(filhoIdStr));
    
    if (!filhosIdsStr.includes(filhoIdStr)) {
      const newFilhosIds = [...formData.filhos_ids, filhoIdStr];
      console.log('✅ Adicionando filho. Novo array:', newFilhosIds);
      setFormData({
        ...formData,
        filhos_ids: newFilhosIds,
        qtd_filhos: newFilhosIds.length,
      });
      console.log('✅ Estado atualizado!');
    } else {
      console.log('⚠️ Filho não adicionado - já existe no array');
    }
  };

  const handleRemoveFilho = (filhoId) => {
    const newFilhosIds = formData.filhos_ids.filter(id => id !== filhoId);
    setFormData({
      ...formData,
      filhos_ids: newFilhosIds,
      qtd_filhos: newFilhosIds.length,
    });
  };

  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione um arquivo de imagem');
        return;
      }
      // Validar tamanho (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A imagem deve ter no máximo 5MB');
        return;
      }
      setFoto(file);
      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBuscarCep = async () => {
    const cep = formData.endereco.cep?.replace(/\D/g, '');
    if (cep?.length !== 8) {
      toast.error('CEP inválido');
      return;
    }

    setLoadingCep(true);
    try {
      const response = await utilsApi.buscarCep(cep);
      setFormData({
        ...formData,
        endereco: {
          ...formData.endereco,
          logradouro: response.data.logradouro || '',
          bairro: response.data.bairro || '',
          cidade: response.data.cidade || '',
          estado: response.data.estado || '',
        },
      });
      toast.success('Endereço encontrado!');
    } catch (error) {
      toast.error('CEP não encontrado');
    } finally {
      setLoadingCep(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome) {
      toast.error('Nome é obrigatório');
      return;
    }

    if (!formData.igreja_id) {
      toast.error('Selecione uma igreja');
      return;
    }

    setLoading(true);
    try {
      // Preparar dados removendo máscaras dos campos que precisam
      const cleanData = { ...formData };
      if (cleanData.cpf) {
        cleanData.cpf = removeMask(cleanData.cpf);
      }
      if (cleanData.rg) {
        cleanData.rg = removeMask(cleanData.rg);
      }
      if (cleanData.telefone_residencial) {
        cleanData.telefone_residencial = removeMask(cleanData.telefone_residencial);
      }
      if (cleanData.telefone_whatsapp) {
        cleanData.telefone_whatsapp = removeMask(cleanData.telefone_whatsapp);
      }

      // Construir objeto grupo_familiar
      const grupoFamiliar = {
        id: cleanData.grupo_familiar?.id || null,
        // id_cabeca: será o ID do membro atual se for cabeça da família
        // Se estiver editando, usar o ID atual. Se criando, será definido pelo backend
        id_cabeca: cleanData.cabeca_familia ? (isEditing ? id : null) : null,
        conjuge_id: cleanData.conjuge_membro && cleanData.conjuge_id ? cleanData.conjuge_id : null,
        filhos: cleanData.cabeca_familia && cleanData.filhos_ids && cleanData.filhos_ids.length > 0
          ? cleanData.filhos_ids.map(filhoId => filhoId)
          : [],
        id_pai: cleanData.id_pai || null,
        id_mae: cleanData.id_mae || null,
      };

      // Adicionar grupo_familiar aos dados
      cleanData.grupo_familiar = grupoFamiliar;

      // Ajustar campos de relacionamento familiar conforme regras
      // No cadastro/alteração: conjuge_id será enviado SOMENTE se conjuge_membro for true
      if (cleanData.conjuge_membro) {
        // Se cônjuge é membro: enviar conjuge_id na requisição, não enviar conjuge_nome
        if (cleanData.conjuge_id) {
          // Manter conjuge_id para enviar na requisição
          cleanData.conjuge_id = cleanData.conjuge_id;
        } else {
          delete cleanData.conjuge_id;
        }
        delete cleanData.conjuge_nome;
      } else {
        // Se cônjuge não é membro: enviar conjuge_nome, não enviar conjuge_id
        if (cleanData.conjuge_nome) {
          cleanData.conjuge_nome = cleanData.conjuge_nome;
        } else {
          delete cleanData.conjuge_nome;
        }
        delete cleanData.conjuge_id;
      }

      // Ajustar campos de filhos
      // Quando filho_membro for marcado, enviar filho_membro: true e filhos: [{id: "12345"}]
      console.log('🔍 DEBUG - Antes de processar filhos:');
      console.log('  - filho_membro:', cleanData.filho_membro);
      console.log('  - filhos_ids:', cleanData.filhos_ids);
      console.log('  - filhos_ids length:', cleanData.filhos_ids?.length);
      
      if (cleanData.filho_membro) {
        console.log('✅ filho_membro está TRUE - processando...');
        // Sempre enviar filho_membro: true quando marcado
        cleanData.filho_membro = true;
        
        // Se houver filhos vinculados, enviar array no formato [{id: "12345"}]
        if (cleanData.filhos_ids && cleanData.filhos_ids.length > 0) {
          console.log('✅ Há filhos vinculados:', cleanData.filhos_ids);
          cleanData.filhos = cleanData.filhos_ids.map(filhoId => ({
            id: filhoId
          }));
          cleanData.qtd_filhos = cleanData.filhos_ids.length;
          console.log('✅ Array filhos criado:', cleanData.filhos);
        } else {
          console.log('⚠️ filho_membro TRUE mas sem filhos - enviando array vazio');
          // Se não houver filhos, enviar array vazio
          cleanData.filhos = [];
        }
        
        // Ajustar filhos no grupo_familiar também (para cabeça da família)
        if (cleanData.cabeca_familia && cleanData.filhos_ids && cleanData.filhos_ids.length > 0) {
          grupoFamiliar.filhos = cleanData.filhos_ids.map(filhoId => filhoId);
        }
        
        // Remover filhos_ids do objeto (já está em filhos)
        delete cleanData.filhos_ids;
        console.log('✅ Após processar - filho_membro:', cleanData.filho_membro, 'filhos:', cleanData.filhos);
      } else {
        console.log('❌ filho_membro está FALSE - removendo campos');
        // Se filho_membro não estiver marcado: não enviar
        delete cleanData.filho_membro;
        delete cleanData.filhos;
        delete cleanData.filhos_ids;
        
        // Limpar filhos do grupo_familiar se não for cabeça da família
        if (!cleanData.cabeca_familia) {
          grupoFamiliar.filhos = [];
        }
      }

      // Remover campos que não devem ser enviados diretamente
      // grupo_familiar é apenas para consulta, não deve ser enviado nas requisições
      delete cleanData.grupo_familiar;
      delete cleanData.tem_grupo_familiar;
      delete cleanData.grupo_familiar_id;
      delete cleanData.id; // ID não deve ser enviado no update/create
      delete cleanData.created_at; // Timestamp não deve ser enviado
      delete cleanData.idade; // Campo calculado, não deve ser enviado
      delete cleanData.codigo; // Código é gerado pelo backend

      // Criar FormData se houver foto, senão usar JSON normal
      let dataToSend;
      if (foto) {
        dataToSend = new FormData();
        // Adicionar todos os campos do cleanData
        Object.keys(cleanData).forEach((key) => {
          if (key === 'endereco') {
            // Adicionar campos de endereço separadamente
            Object.keys(cleanData.endereco).forEach((endKey) => {
              const endValue = cleanData.endereco[endKey];
              if (endValue !== null && endValue !== undefined && endValue !== '') {
                dataToSend.append(`endereco.${endKey}`, endValue);
              }
            });
          } else if (key === 'foto_url') {
            // Não enviar foto_url se estamos enviando uma nova foto
            // O backend deve usar a nova foto enviada no campo 'foto'
          } else if (key === 'grupo_familiar') {
            // Enviar grupo_familiar como JSON string
            if (cleanData.grupo_familiar) {
              dataToSend.append(key, JSON.stringify(cleanData.grupo_familiar));
            }
          } else if (key === 'filhos') {
            // Enviar array de filhos como JSON string (mesmo se vazio quando filho_membro for true)
            if (Array.isArray(cleanData.filhos)) {
              console.log('📤 Adicionando filhos ao FormData:', JSON.stringify(cleanData.filhos));
              dataToSend.append(key, JSON.stringify(cleanData.filhos));
            } else {
              console.log('⚠️ filhos não é array:', cleanData.filhos);
            }
          } else if (cleanData[key] !== null && cleanData[key] !== undefined) {
            // Para booleanos, enviar mesmo se for false
            if (typeof cleanData[key] === 'boolean') {
              if (key === 'filho_membro') {
                console.log('📤 Adicionando filho_membro ao FormData:', cleanData[key]);
              }
              dataToSend.append(key, cleanData[key]);
            } else if (typeof cleanData[key] === 'number') {
              dataToSend.append(key, cleanData[key].toString());
            } else if (Array.isArray(cleanData[key])) {
              // Se for array, enviar como JSON string
              dataToSend.append(key, JSON.stringify(cleanData[key]));
            } else if (cleanData[key] !== '') {
              dataToSend.append(key, cleanData[key]);
            }
          }
        });
        // Adicionar a foto no campo 'foto' da requisição
        dataToSend.append('foto', foto);
        
        console.log('📤 FormData final - verificando campos:');
        console.log('  - filho_membro no FormData:', dataToSend.has('filho_membro') ? dataToSend.get('filho_membro') : 'NÃO ENCONTRADO');
        console.log('  - filhos no FormData:', dataToSend.has('filhos') ? dataToSend.get('filhos') : 'NÃO ENCONTRADO');
        
        // Log de todos os campos do FormData
        console.log('📋 Todos os campos do FormData:');
        for (let pair of dataToSend.entries()) {
          if (pair[0] === 'filho_membro' || pair[0] === 'filhos' || pair[0].includes('filho')) {
            console.log(`  - ${pair[0]}:`, pair[1]);
          }
        }
      } else {
        console.log('📤 Criando JSON (sem foto)');
        dataToSend = cleanData;
        console.log('📤 JSON final - campos relevantes:', {
          filho_membro: dataToSend.filho_membro,
          filhos: dataToSend.filhos,
          qtd_filhos: dataToSend.qtd_filhos
        });
        console.log('📤 JSON final - filhos expandido:', JSON.stringify(dataToSend.filhos, null, 2));
        console.log('📤 JSON final - filho_membro tipo:', typeof dataToSend.filho_membro);
        console.log('📤 JSON final - filho_membro valor:', dataToSend.filho_membro);
        console.log('📤 JSON completo que será enviado:', JSON.stringify({
          filho_membro: dataToSend.filho_membro,
          filhos: dataToSend.filhos,
          qtd_filhos: dataToSend.qtd_filhos,
          nome: dataToSend.nome
        }, null, 2));
      }

      // Verificação final antes de enviar
      console.log('🚀 Enviando requisição...');
      if (dataToSend instanceof FormData) {
        console.log('🚀 dataToSend é FormData');
        console.log('🚀 FormData - filho_membro:', dataToSend.has('filho_membro') ? dataToSend.get('filho_membro') : 'NÃO ENCONTRADO');
        console.log('🚀 FormData - filhos:', dataToSend.has('filhos') ? dataToSend.get('filhos') : 'NÃO ENCONTRADO');
        
        // Log completo do FormData
        console.log('📋 REQUISIÇÃO COMPLETA - FormData:');
        for (let pair of dataToSend.entries()) {
          console.log(`  - ${pair[0]}:`, pair[1]);
        }
      } else {
        console.log('🚀 dataToSend é JSON');
        console.log('🚀 JSON final - filho_membro:', dataToSend.filho_membro);
        console.log('🚀 JSON final - filhos:', JSON.stringify(dataToSend.filhos, null, 2));
        console.log('🚀 JSON final - qtd_filhos:', dataToSend.qtd_filhos);
        
        // Log completo do JSON
        console.log('📋 REQUISIÇÃO COMPLETA - JSON:');
        console.log(JSON.stringify(dataToSend, null, 2));
        
        // Garantir que os campos estão presentes
        if (dataToSend.filho_membro === true) {
          console.log('✅ filho_membro confirmado como true no JSON final');
          if (!dataToSend.filhos) {
            console.log('⚠️ ATENÇÃO: filho_membro é true mas filhos não existe! Criando array vazio...');
            dataToSend.filhos = [];
          }
          if (!Array.isArray(dataToSend.filhos)) {
            console.log('⚠️ ATENÇÃO: filhos não é array! Convertendo...');
            dataToSend.filhos = Array.isArray(dataToSend.filhos) ? dataToSend.filhos : [];
          }
        }
      }
      
      if (isEditing) {
        console.log('📝 Atualizando membro ID:', id);
        const response = await membrosApi.update(id, dataToSend);
        console.log('✅ Resposta do update:', response?.data);
        toast.success('Membro atualizado com sucesso!');
      } else {
        console.log('➕ Criando novo membro');
        const response = await membrosApi.create(dataToSend);
        console.log('✅ Resposta do create:', response?.data);
        toast.success('Membro cadastrado com sucesso!');
      }
      navigate('/membros');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar membro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="membro-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/membros')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="page-title">{isEditing ? 'Editar Membro' : 'Novo Membro'}</h1>
          <p className="page-subtitle">
            {isEditing ? 'Atualize os dados do membro' : 'Preencha os dados do novo membro'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="pessoal" className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="pessoal">Dados Pessoais</TabsTrigger>
            <TabsTrigger value="eclesiastico">Eclesiástico</TabsTrigger>
            <TabsTrigger value="familia">Família</TabsTrigger>
            <TabsTrigger value="contato">Contato</TabsTrigger>
            <TabsTrigger value="profissional">Profissional</TabsTrigger>
          </TabsList>

          {/* Dados Pessoais */}
          <TabsContent value="pessoal">
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="card-title">Identificação</CardTitle>
                <CardDescription>Informações básicas do membro</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="foto">Foto</Label>
                    <div className="mt-1 space-y-2">
                      {fotoPreview && (
                        <div className="relative inline-block">
                          <img
                            src={fotoPreview}
                            alt="Preview"
                            className="h-32 w-32 object-cover rounded-lg border-2 border-slate-200"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                            onClick={() => {
                              setFoto(null);
                              setFotoPreview(null);
                              setFormData({ ...formData, foto_url: '' });
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      <div className="flex items-center gap-4">
                        <Input
                          id="foto"
                          name="foto"
                          type="file"
                          accept="image/*"
                          onChange={handleFotoChange}
                          className="input-field cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
                          data-testid="input-foto"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Formatos aceitos: JPG, PNG, GIF (máx. 5MB)</p>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="nome">Nome Completo *</Label>
                    <Input
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                      className="input-field mt-1"
                      data-testid="input-nome"
                    />
                  </div>

                  <div>
                    <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                    <Input
                      id="data_nascimento"
                      name="data_nascimento"
                      type="date"
                      value={formData.data_nascimento}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-data-nascimento"
                    />
                  </div>

                  <div>
                    <Label htmlFor="sexo">Sexo</Label>
                    <Select value={formData.sexo} onValueChange={(v) => handleSelectChange('sexo', v)}>
                      <SelectTrigger className="input-field mt-1" data-testid="select-sexo">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Masculino</SelectItem>
                        <SelectItem value="F">Feminino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="estado_civil">Estado Civil</Label>
                    <Select value={formData.estado_civil} onValueChange={(v) => handleSelectChange('estado_civil', v)}>
                      <SelectTrigger className="input-field mt-1" data-testid="select-estado-civil">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {estadoCivilOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="igreja_id">Igreja *</Label>
                    <Select value={formData.igreja_id} onValueChange={(v) => handleSelectChange('igreja_id', v)}>
                      <SelectTrigger className="input-field mt-1" data-testid="select-igreja">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {igrejas.map((igreja) => (
                          <SelectItem key={igreja.id} value={igreja.id}>{igreja.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="cpf">CPF</Label>
                    <Input
                      id="cpf"
                      name="cpf"
                      value={formData.cpf}
                      onChange={(e) => handleMaskedChange(e, maskCPF)}
                      className="input-field mt-1"
                      placeholder="000.000.000-00"
                      maxLength={14}
                      data-testid="input-cpf"
                    />
                  </div>

                  <div>
                    <Label htmlFor="rg">RG</Label>
                    <Input
                      id="rg"
                      name="rg"
                      value={formData.rg}
                      onChange={(e) => handleMaskedChange(e, maskRG)}
                      className="input-field mt-1"
                      placeholder="00.000.000-0"
                      maxLength={12}
                      data-testid="input-rg"
                    />
                  </div>

                  <div>
                    <Label htmlFor="orgao_rg">Órgão Emissor</Label>
                    <Input
                      id="orgao_rg"
                      name="orgao_rg"
                      value={formData.orgao_rg}
                      onChange={handleChange}
                      className="input-field mt-1"
                      placeholder="SSP/SP"
                      data-testid="input-orgao-rg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="naturalidade">Naturalidade</Label>
                    <Input
                      id="naturalidade"
                      name="naturalidade"
                      value={formData.naturalidade}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-naturalidade"
                    />
                  </div>

                  <div>
                    <Label htmlFor="nacionalidade">Nacionalidade</Label>
                    <Input
                      id="nacionalidade"
                      name="nacionalidade"
                      value={formData.nacionalidade}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-nacionalidade"
                    />
                  </div>

                  <div>
                    <Label htmlFor="tipo_sanguineo">Tipo Sanguíneo</Label>
                    <Select value={formData.tipo_sanguineo} onValueChange={(v) => handleSelectChange('tipo_sanguineo', v)}>
                      <SelectTrigger className="input-field mt-1" data-testid="select-tipo-sanguineo">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {tipoSanguineoOptions.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="grau_instrucao">Grau de Instrução</Label>
                    <Select value={formData.grau_instrucao} onValueChange={(v) => handleSelectChange('grau_instrucao', v)}>
                      <SelectTrigger className="input-field mt-1" data-testid="select-grau-instrucao">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {grauInstrucaoOptions.map((g) => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="formacao_academica">Formação Acadêmica</Label>
                    <Input
                      id="formacao_academica"
                      name="formacao_academica"
                      value={formData.formacao_academica}
                      onChange={handleChange}
                      className="input-field mt-1"
                      placeholder="Ex: Administração"
                      data-testid="input-formacao"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dados Eclesiásticos */}
          <TabsContent value="eclesiastico">
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="card-title">Dados Eclesiásticos</CardTitle>
                <CardDescription>Informações ministeriais</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="obreiro"
                      checked={formData.obreiro}
                      onCheckedChange={(c) => handleCheckboxChange('obreiro', c)}
                      data-testid="checkbox-obreiro"
                    />
                    <Label htmlFor="obreiro">É Obreiro?</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="deseja_recibo_digital"
                      checked={formData.deseja_recibo_digital}
                      onCheckedChange={(c) => handleCheckboxChange('deseja_recibo_digital', c)}
                      data-testid="checkbox-recibo"
                    />
                    <Label htmlFor="deseja_recibo_digital">Deseja Recibo Digital?</Label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cargo_id">Cargo</Label>
                    <Select value={formData.cargo_id || ''} onValueChange={(v) => handleSelectChange('cargo_id', v)}>
                      <SelectTrigger className="input-field mt-1" data-testid="select-cargo">
                        <SelectValue placeholder="Selecione um cargo" />
                      </SelectTrigger>
                      <SelectContent>
                        {cargos.map((cargo) => (
                          <SelectItem key={cargo.id} value={cargo.id.toString()}>
                            {cargo.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="data_batismo">Data de Batismo</Label>
                    <Input
                      id="data_batismo"
                      name="data_batismo"
                      type="date"
                      value={formData.data_batismo}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-data-batismo"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Família */}
          <TabsContent value="familia">
            <div className="space-y-6">
              <Card className="stat-card">
                <CardHeader>
                  <CardTitle className="card-title">Dados Familiares</CardTitle>
                  <CardDescription>Informações da família</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nome_pai">Nome do Pai</Label>
                      <Input
                        id="nome_pai"
                        name="nome_pai"
                        value={formData.nome_pai}
                        onChange={handleChange}
                        className="input-field mt-1"
                        data-testid="input-nome-pai"
                      />
                    </div>

                    <div>
                      <Label htmlFor="nome_mae">Nome da Mãe</Label>
                      <Input
                        id="nome_mae"
                        name="nome_mae"
                        value={formData.nome_mae}
                        onChange={handleChange}
                        className="input-field mt-1"
                        data-testid="input-nome-mae"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="conjuge">
                        {formData.conjuge_membro ? 'Cônjuge (Membro)' : 'Nome do Cônjuge'}
                      </Label>
                      {formData.conjuge_membro ? (
                        <Select
                          value={formData.conjuge_id || ''}
                          onValueChange={(v) => {
                            setFormData({
                              ...formData,
                              conjuge_id: v,
                              conjuge_nome: '', // Limpar nome quando selecionar por ID
                            });
                          }}
                        >
                          <SelectTrigger className="input-field mt-1" data-testid="select-conjuge">
                            <SelectValue placeholder="Selecione o cônjuge" />
                          </SelectTrigger>
                          <SelectContent>
                            {membros.map((membro) => (
                              <SelectItem key={membro.id} value={membro.id.toString()}>
                                {membro.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id="conjuge_nome"
                          name="conjuge_nome"
                          value={formData.conjuge_nome}
                          onChange={handleChange}
                          className="input-field mt-1"
                          data-testid="input-conjuge"
                          placeholder="Digite o nome do cônjuge"
                        />
                      )}
                    </div>

                    <div>
                      <Label htmlFor="data_casamento">Data de Casamento</Label>
                      <Input
                        id="data_casamento"
                        name="data_casamento"
                        type="date"
                        value={formData.data_casamento}
                        onChange={handleChange}
                        className="input-field mt-1"
                        data-testid="input-data-casamento"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="qtd_filhos">Quantidade de Filhos</Label>
                      <Input
                        id="qtd_filhos"
                        name="qtd_filhos"
                        type="number"
                        min="0"
                        value={formData.qtd_filhos}
                        onChange={handleChange}
                        className="input-field mt-1"
                        data-testid="input-qtd-filhos"
                        readOnly={formData.filho_membro && formData.filhos_ids && formData.filhos_ids.length > 0}
                        disabled={formData.filho_membro && formData.filhos_ids && formData.filhos_ids.length > 0}
                      />
                      {formData.filho_membro && formData.filhos_ids && formData.filhos_ids.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          Preenchido automaticamente pelos filhos membros vinculados ({formData.filhos_ids.length})
                        </p>
                      )}
                    </div>

                    <div className="flex items-end gap-2 pb-2">
                      <Checkbox
                        id="conjuge_membro"
                        checked={formData.conjuge_membro}
                        onCheckedChange={(c) => handleCheckboxChange('conjuge_membro', c)}
                        data-testid="checkbox-conjuge-membro"
                      />
                      <Label htmlFor="conjuge_membro">Cônjuge é membro?</Label>
                    </div>

                    <div className="flex items-end gap-2 pb-2">
                      <Checkbox
                        id="cabeca_familia"
                        checked={formData.cabeca_familia}
                        onCheckedChange={(c) => handleCheckboxChange('cabeca_familia', c)}
                        data-testid="checkbox-cabeca-familia"
                      />
                      <Label htmlFor="cabeca_familia">Cabeça da Família?</Label>
                    </div>

                    <div className="flex items-end gap-2 pb-2">
                      <Checkbox
                        id="filho_membro"
                        checked={formData.filho_membro}
                        onCheckedChange={(c) => handleCheckboxChange('filho_membro', c)}
                        data-testid="checkbox-filho-membro"
                      />
                      <Label htmlFor="filho_membro">Filho é membro?</Label>
                    </div>
                  </div>

                  {/* Vincular Filhos que são Membros - Aparece se filho_membro for true */}
                  {formData.filho_membro && (
                    <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <Label className="text-base font-semibold">Filhos que são Membros</Label>
                        <p className="text-sm text-slate-500 mt-1">
                          Vincule filhos que também são membros da igreja
                        </p>
                      </div>
                      <Select
                        value=""
                        onValueChange={(value) => {
                          console.log('🎯 Select onValueChange chamado com value:', value);
                          if (value) {
                            handleAddFilho(value);
                            // Resetar o select após adicionar
                            setTimeout(() => {
                              // Forçar reset do select
                              const select = document.querySelector('[data-testid="select-filho"]');
                              if (select) {
                                select.value = '';
                              }
                            }, 100);
                          }
                        }}
                      >
                        <SelectTrigger className="w-[250px]" data-testid="select-filho">
                          <SelectValue placeholder="Adicionar filho membro" />
                        </SelectTrigger>
                        <SelectContent>
                          {membros
                            .filter(m => {
                              const membroIdStr = m.id.toString();
                              const filhosIdsStr = formData.filhos_ids.map(id => id.toString());
                              return !filhosIdsStr.includes(membroIdStr);
                            })
                            .map((membro) => (
                              <SelectItem key={membro.id} value={membro.id.toString()}>
                                {membro.nome}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.filhos_ids.length > 0 && (
                      <div className="space-y-2">
                        {formData.filhos_ids.map((filhoId) => {
                          // Buscar filho na lista de membros (comparar como string e número)
                          const filho = membros.find(m => 
                            m.id === filhoId || 
                            m.id === parseInt(filhoId) || 
                            m.id?.toString() === filhoId?.toString()
                          );
                          if (!filho) {
                            // Se não encontrar na lista, ainda mostrar o ID para debug
                            return (
                              <div
                                key={filhoId}
                                className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                    <span className="text-yellow-600 font-semibold text-sm">?</span>
                                  </div>
                                  <div>
                                    <p className="font-medium text-yellow-900">Filho não encontrado</p>
                                    <p className="text-xs text-yellow-700">ID: {filhoId}</p>
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveFilho(filhoId)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          }
                          return (
                            <div
                              key={filhoId}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                            >
                              <div className="flex items-center gap-3">
                                {filho.foto_url ? (
                                  <img
                                    src={filho.foto_url}
                                    alt={filho.nome}
                                    className="w-10 h-10 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                                    <span className="text-sky-600 font-semibold text-sm">
                                      {filho.nome?.charAt(0)?.toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-slate-900">{filho.nome}</p>
                                  <p className="text-xs text-slate-500">Filho(a)</p>
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveFilho(filhoId)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Visualização de Vínculos Familiares */}
              {(formData.conjuge_id || formData.filhos_ids.length > 0 || formData.conjuge_nome) && (
                <Card className="stat-card">
                  <CardHeader>
                    <CardTitle className="card-title flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Vínculos Familiares
                    </CardTitle>
                    <CardDescription>Visualização dos relacionamentos familiares</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Cônjuge */}
                      {formData.conjuge_id && (
                        <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Users className="h-6 w-6 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-blue-900">
                              {membros.find(m => 
                                m.id === formData.conjuge_id || 
                                m.id === parseInt(formData.conjuge_id) || 
                                m.id?.toString() === formData.conjuge_id?.toString()
                              )?.nome || formData.conjuge_nome}
                            </p>
                            <p className="text-sm text-blue-700">Cônjuge</p>
                          </div>
                          {formData.conjuge_membro && (
                            <span className="px-2 py-1 text-xs font-medium bg-blue-200 text-blue-800 rounded">
                              Membro
                            </span>
                          )}
                        </div>
                      )}

                      {formData.conjuge_nome && !formData.conjuge_id && (
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <Users className="h-6 w-6 text-slate-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{formData.conjuge_nome}</p>
                            <p className="text-sm text-slate-600">Cônjuge (não membro)</p>
                          </div>
                        </div>
                      )}

                      {/* Filhos */}
                      {formData.filhos_ids.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-slate-700 mb-2">Filhos Membros ({formData.filhos_ids.length})</h4>
                          {formData.filhos_ids.map((filhoId) => {
                            // Buscar filho na lista de membros (comparar como string e número)
                            const filho = membros.find(m => 
                              m.id === filhoId || 
                              m.id === parseInt(filhoId) || 
                              m.id?.toString() === filhoId?.toString()
                            );
                            if (!filho) {
                              // Se não encontrar, mostrar mensagem
                              return (
                                <div
                                  key={filhoId}
                                  className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                                >
                                  <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-yellow-600 font-semibold">?</span>
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-semibold text-yellow-900">Filho não encontrado</p>
                                    <p className="text-sm text-yellow-700">ID: {filhoId}</p>
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={filhoId}
                                className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200"
                              >
                                {filho.foto_url ? (
                                  <img
                                    src={filho.foto_url}
                                    alt={filho.nome}
                                    className="w-12 h-12 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-green-600 font-semibold">
                                      {filho.nome?.charAt(0)?.toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                <div className="flex-1">
                                  <p className="font-semibold text-green-900">{filho.nome}</p>
                                  <p className="text-sm text-green-700">Filho(a)</p>
                                </div>
                                <span className="px-2 py-1 text-xs font-medium bg-green-200 text-green-800 rounded">
                                  Membro
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Indicador de Cabeça da Família */}
                      {formData.cabeca_familia && (
                        <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-sm font-medium text-amber-900 flex items-center gap-2">
                            <UserPlus className="h-4 w-4" />
                            Esta pessoa é o(a) cabeça da família
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Contato */}
          <TabsContent value="contato">
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="card-title">Contato e Endereço</CardTitle>
                <CardDescription>Informações de contato</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="telefone_residencial">Telefone Residencial</Label>
                    <Input
                      id="telefone_residencial"
                      name="telefone_residencial"
                      value={formData.telefone_residencial}
                      onChange={(e) => handleMaskedChange(e, maskTelefone)}
                      className="input-field mt-1"
                      placeholder="(00) 0000-0000"
                      maxLength={15}
                      data-testid="input-tel-residencial"
                    />
                  </div>

                  <div>
                    <Label htmlFor="telefone_whatsapp">WhatsApp</Label>
                    <Input
                      id="telefone_whatsapp"
                      name="telefone_whatsapp"
                      value={formData.telefone_whatsapp}
                      onChange={(e) => handleMaskedChange(e, maskTelefone)}
                      className="input-field mt-1"
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                      data-testid="input-whatsapp"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-1">
                    <Label htmlFor="endereco.cep">CEP</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="endereco.cep"
                        name="endereco.cep"
                        value={formData.endereco.cep}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="00000-000"
                        data-testid="input-cep"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleBuscarCep}
                        disabled={loadingCep}
                        data-testid="btn-buscar-cep"
                      >
                        {loadingCep ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="md:col-span-3">
                    <Label htmlFor="endereco.logradouro">Logradouro</Label>
                    <Input
                      id="endereco.logradouro"
                      name="endereco.logradouro"
                      value={formData.endereco.logradouro}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-logradouro"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="endereco.numero">Número</Label>
                    <Input
                      id="endereco.numero"
                      name="endereco.numero"
                      value={formData.endereco.numero}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-numero"
                    />
                  </div>

                  <div>
                    <Label htmlFor="endereco.complemento">Complemento</Label>
                    <Input
                      id="endereco.complemento"
                      name="endereco.complemento"
                      value={formData.endereco.complemento}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-complemento"
                    />
                  </div>

                  <div>
                    <Label htmlFor="endereco.bairro">Bairro</Label>
                    <Input
                      id="endereco.bairro"
                      name="endereco.bairro"
                      value={formData.endereco.bairro}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-bairro"
                    />
                  </div>

                  <div>
                    <Label htmlFor="endereco.cidade">Cidade</Label>
                    <Input
                      id="endereco.cidade"
                      name="endereco.cidade"
                      value={formData.endereco.cidade}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-cidade"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="endereco.estado">Estado</Label>
                    <Input
                      id="endereco.estado"
                      name="endereco.estado"
                      value={formData.endereco.estado}
                      onChange={handleChange}
                      className="input-field mt-1"
                      placeholder="UF"
                      maxLength={2}
                      data-testid="input-estado"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profissional */}
          <TabsContent value="profissional">
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="card-title">Dados Profissionais</CardTitle>
                <CardDescription>Informações profissionais</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="profissao">Profissão</Label>
                    <Input
                      id="profissao"
                      name="profissao"
                      value={formData.profissao}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-profissao"
                    />
                  </div>

                  <div>
                    <Label htmlFor="empresa">Empresa</Label>
                    <Input
                      id="empresa"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      className="input-field mt-1"
                      data-testid="input-empresa"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Submit Button */}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/membros')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="btn-primary"
            disabled={loading}
            data-testid="submit-membro"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEditing ? 'Atualizar' : 'Cadastrar'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MembroForm;
